import { Link } from "react-router-dom";
import { MapPin, Briefcase, Clock, Globe } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { JobPost, formatJobDate } from "@/data/jobPosts";
import { useLanguage } from "@/hooks/useLanguage";

interface JobDetailHeroProps {
  job: JobPost;
}

const JobDetailHero = ({ job }: JobDetailHeroProps) => {
  const { t, localizePath, language } = useLanguage();

  return (
    <section className="bg-secondary/30 pt-40 pb-16">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl">
          <Breadcrumb className="mb-8">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={localizePath("/")} className="text-muted-foreground hover:text-accent">
                    {t("careers.jobDetail.home")}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={localizePath("/karriere")} className="text-muted-foreground hover:text-accent">
                    {t("careers.jobDetail.karriere")}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-accent">{job.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge variant="secondary" className="bg-accent/10 text-accent">
              {job.department}
            </Badge>
            {job.remote && (
              <Badge variant="outline" className="text-muted-foreground">
                <Globe className="w-3 h-3 mr-1" />
                {t("careers.jobDetail.remotePossible")}
              </Badge>
            )}
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-6">
            {job.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-muted-foreground">
            <span className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {job.location}
            </span>
            <span className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              {job.type}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {t("careers.jobDetail.publishedOn")} {formatJobDate(job.publishDate, language)}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JobDetailHero;
